import React, { createContext, useEffect, useReducer, useRef, useState } from 'react';

import { jwtDecode } from 'jwt-decode';

import { LOGIN, LOGOUT } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';

import Loader from 'components/Loader';
import { KeyedObject } from 'types/root';
import { AuthProps, JWTContextType } from 'types/auth';
import { authApi } from '../api/authApi';
import { AppConfig } from '../config';
import { useNavigate } from 'react-router-dom';
import { TokenRefreshService } from '../services/tokenRefreshService';

const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

const verifyToken: (st: string) => boolean = (serviceToken) => {
  if (!serviceToken) {
    return false;
  }

  try {
    const decoded: KeyedObject = jwtDecode(serviceToken);
    return decoded.exp > Date.now() / 1000;
  } catch (error) {
    return false;
  }
};

const setSession = (serviceToken?: string | null) => {
  if (serviceToken) {
    localStorage.setItem(AppConfig.tokenKey, serviceToken);
  } else {
    localStorage.removeItem(AppConfig.tokenKey);
  }
};

const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const navigate = useNavigate();
  const [isTokenChecked, setIsTokenChecked] = useState(false);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleLogout = () => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    setSession(null);
    localStorage.removeItem(AppConfig.adminTokenKey);
    dispatch({ type: LOGOUT });
    navigate('/login');
  };

  const monitorToken = (serviceToken: string) => {
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    try {
      const decoded: KeyedObject = jwtDecode(serviceToken);
      const expirationTime = decoded.exp * 1000;
      const currentTime = Date.now();
      const bufferTime = 15 * 1000;
      const timeRemaining = expirationTime - currentTime;

      if (timeRemaining > bufferTime) {
        logoutTimerRef.current = setTimeout(handleLogout, timeRemaining - bufferTime);
      } else {
        handleLogout();
      }
    } catch (error) {
      handleLogout();
    }
  };

  const init = async () => {
    try {
      const serviceToken = localStorage.getItem(AppConfig.tokenKey);

      if (serviceToken && verifyToken(serviceToken)) {
        setSession(serviceToken);
        const user = await authApi.me();

        dispatch({
          type: LOGIN,
          payload: {
            isLoggedIn: true,
            user,
          },
        });

        monitorToken(serviceToken);
      } else {
        setSession(null);
        localStorage.removeItem(AppConfig.adminTokenKey);
        dispatch({ type: LOGOUT });
      }
    } catch (error) {
      handleLogout();
    } finally {
      setIsTokenChecked(true);
    }
  };

  useEffect(() => {
    init();

    const tokenSub = TokenRefreshService.onTokenUpdated().subscribe((newToken) => {
      monitorToken(newToken);
    });

    const handleCrossTabTokenChange = (e: StorageEvent) => {
      if (e.storageArea !== localStorage) return;
      if (e.key !== AppConfig.tokenKey) return;
      if (!e.newValue) {
        handleLogout();
        return;
      }
      try {
        const newUserId = jwtDecode<{ user?: { id?: string } }>(e.newValue).user?.id;
        const oldUserId = e.oldValue ? jwtDecode<{ user?: { id?: string } }>(e.oldValue).user?.id : null;
        if (newUserId !== oldUserId) {
          window.location.reload();
        }
      } catch {
        handleLogout();
      }
    };
    window.addEventListener('storage', handleCrossTabTokenChange);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const token = localStorage.getItem(AppConfig.tokenKey);
      if (!token || !verifyToken(token)) handleLogout();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      tokenSub.unsubscribe();
      window.removeEventListener('storage', handleCrossTabTokenChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const login = async (email: string, password: string) => {
    const result = await authApi.login(email, password);

    if (result.action === 'authenticated') {
      await completeLogin(result.accessToken);
      return;
    }

    throw result;
  };

  const completeLogin = async (token: string) => {
    setSession(token);
    const user = await authApi.me();
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user,
      },
    });
    monitorToken(token);
    navigate('/', { replace: true, state: { freshLogin: true } });
  };

  const logout = () => {
    handleLogout();
  };

  const resetPassword = async (email: string) => {
    await authApi.forgotPassword(email);
  };

  const updateProfile = () => {};

  const startImpersonation = async (targetUserId: string) => {
    if (localStorage.getItem(AppConfig.adminTokenKey)) {
      throw new Error('Already impersonating; return to your admin account first.');
    }
    const currentToken = localStorage.getItem(AppConfig.tokenKey);
    if (currentToken) {
      localStorage.setItem(AppConfig.adminTokenKey, currentToken);
    }
    const result = await authApi.impersonate(targetUserId);
    await completeLogin(result.accessToken);
  };

  const stopImpersonation = () => {
    const adminToken = localStorage.getItem(AppConfig.adminTokenKey);
    localStorage.removeItem(AppConfig.adminTokenKey);
    if (adminToken && verifyToken(adminToken)) {
      setSession(adminToken);
      window.location.href = '/';
    } else {
      handleLogout();
    }
  };

  const isImpersonating = () => !!localStorage.getItem(AppConfig.adminTokenKey);

  if (!isTokenChecked) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
        completeLogin,
        resetPassword,
        updateProfile,
        startImpersonation,
        stopImpersonation,
        isImpersonating,
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

export default JWTContext;
